<template>
    <AccordionBasic title="investor_profile_settings">
        <div class="mb-4">
            <p class="mb-2 text-base font-bold">
                {{ $t('show_news_menu_item') }}
            </p>
            <ToggleSwitch
                :model-value="showNewsMenuItem"
                name="show_news_menu_item"
                label="show_news_menu_item"
                help="help_show_news_menu_item"
                @update:model-value="
                    (value) => emit('update:showNewsMenuItem', value)
                "
            />
        </div>
        <div
            v-if="showInheritedPrompt"
            class="space-y-2"
        >
            <p class="text-sm text-gray-500">
                {{ $t('inherited_from_parent') }}.
            </p>
            <button
                type="button"
                class="text-xs font-medium text-blue-600 hover:underline"
                @click="startOverriding"
            >
                {{ $t('investor_profile_settings_override') }}
            </button>
        </div>
        <div
            v-else
            class="space-y-4"
        >
            <div class="flex items-center justify-between">
                <p class="text-base font-bold">{{ $t('personal_data') }}</p>
                <button
                    v-if="parentPlatformName"
                    type="button"
                    class="text-xs font-medium text-gray-500 hover:underline"
                    @click="resetToParent"
                >
                    {{ $t('investor_profile_settings_reset_to_parent') }}
                </button>
            </div>
            <div
                v-for="field in personalDataFields"
                :key="field"
                class="flex flex-wrap items-center justify-between border-b border-gray-200 py-2 last:border-0"
            >
                <div class="w-48 shrink-0 text-sm">{{ $t(field) }}</div>
                <div class="flex flex-wrap gap-4">
                    <ToggleSwitch
                        :model-value="fieldSettings(field).visible"
                        :name="`${field}_visible`"
                        label="field_visible"
                        @update:model-value="
                            (value) => updateField(field, 'visible', value)
                        "
                    />
                    <ToggleSwitch
                        :model-value="fieldSettings(field).required"
                        :name="`${field}_required`"
                        label="field_required"
                        @update:model-value="
                            (value) => updateField(field, 'required', value)
                        "
                    />
                    <ToggleSwitch
                        :model-value="fieldSettings(field).editable_after_kyc"
                        :name="`${field}_editable_after_kyc`"
                        label="field_editable_after_kyc"
                        @update:model-value="
                            (value) =>
                                updateField(field, 'editable_after_kyc', value)
                        "
                    />
                </div>
            </div>
        </div>
        <div class="mt-6">
            <p class="mb-2 text-base font-bold">
                {{ $t('platform_investor_qualification') }}
            </p>
            <SelectField
                :model-value="complianceProfile"
                :options="mapEnumToArray(PlatformComplianceProfile)"
                name="platform_investor_qualification"
                label="platform_investor_qualification"
                help="help_platform_compliance_profile"
                @update:model-value="
                    (value) => emit('update:complianceProfile', value)
                "
            />
        </div>
        <div class="mt-6">
            <p class="mb-2 text-base font-bold">
                {{ $t('show_wallet_menu_item') }}
            </p>
            <ToggleSwitch
                :model-value="showWalletMenuItem"
                name="show_wallet_menu_item"
                label="show_wallet_menu_item"
                help="help_show_wallet_menu_item"
                @update:model-value="
                    (value) => emit('update:showWalletMenuItem', value)
                "
            />
        </div>
    </AccordionBasic>
</template>

<script setup lang="ts">
import AccordionBasic from '@src/components/AccordionBasic.vue'
import SelectField from '@src/components/SelectField.vue'
import ToggleSwitch from '@src/components/ToggleSwitch.vue'
import { useEnum } from '@composables/common'
import { NaturalProfilePersonalDataField } from '@src/enums/Account'
import { PlatformComplianceProfile } from '@src/enums/Platform'
import {
    InvestorProfileFieldSetting,
    InvestorProfileFieldSettings,
} from '@src/types'
import { computed, ref, watch } from 'vue'

const props = defineProps<{
    settings: InvestorProfileFieldSettings | null
    parentPlatformName: string | null
    complianceProfile: PlatformComplianceProfile | null
    showWalletMenuItem: boolean
    showNewsMenuItem: boolean
}>()

const emit = defineEmits<{
    (e: 'update:settings', value: InvestorProfileFieldSettings | null): void
    (
        e: 'update:complianceProfile',
        value: PlatformComplianceProfile | null
    ): void
    (e: 'update:showWalletMenuItem', value: boolean): void
    (e: 'update:showNewsMenuItem', value: boolean): void
}>()

const { mapEnumToArray } = useEnum()

const personalDataFields = Object.values(NaturalProfilePersonalDataField)

// A platform with a parent starts out inheriting the parent's whole configuration (settings
// === null) - the form only appears once the admin explicitly chooses to override it.
const isOverriding = ref(props.settings !== null)
const showInheritedPrompt = computed(
    () => !!props.parentPlatformName && !isOverriding.value
)

const localSettings = ref<InvestorProfileFieldSettings>({
    personal_data: { ...props.settings?.personal_data },
})

watch(
    () => props.settings,
    (newSettings) => {
        isOverriding.value = newSettings !== null
        localSettings.value = {
            personal_data: { ...newSettings?.personal_data },
        }
    },
    { deep: true }
)

function fieldSettings(field: string): InvestorProfileFieldSetting {
    return (
        localSettings.value.personal_data[field] ?? {
            visible: true,
            required: false,
            editable_after_kyc: true,
        }
    )
}

function updateField(
    field: string,
    key: keyof InvestorProfileFieldSetting,
    value: boolean
) {
    localSettings.value.personal_data = {
        ...localSettings.value.personal_data,
        [field]: {
            ...fieldSettings(field),
            [key]: value,
        },
    }
    emit('update:settings', localSettings.value)
}

function startOverriding() {
    isOverriding.value = true
    localSettings.value = { personal_data: {} }
    emit('update:settings', localSettings.value)
}

function resetToParent() {
    isOverriding.value = false
    localSettings.value = { personal_data: {} }
    emit('update:settings', null)
}
</script>
